import { Badge, type BadgeProps } from '@svenskaspelab/ui/react/badge';
import type { ReactNode } from 'react';

import { cn } from '../../../utils/entry.js';
import { DRAW_STATE_TEXTS } from '../../constants.js';
import type { DrawStatus } from '../../utils/getDrawStatus.js';
import shouldShowStatus from '../../utils/shouldShowStatus.js';

function getDrawCardStatus(status: string | undefined): ReactNode {
  if (status === 'Cancelled') {
    return <div>{DRAW_STATE_TEXTS.CANCELLED}</div>;
  }

  if (status === 'Live') {
    return <div>Live</div>;
  }

  if (status === 'Finished') {
    return <div>{DRAW_STATE_TEXTS.FINISHED}</div>;
  }

  if (status === 'Defined') {
    return <div>{DRAW_STATE_TEXTS.NOT_OPENED}</div>;
  }

  if (status === 'TemporaryClosed') {
    return (
      <div className="flex flex-col items-center justify-center font-medium">
        <span className="grow text-sm">Omgången är tillfälligt stängd. Välkommen åter.</span>
        <a href="/installda-matcher" className="ml-1 align-baseline text-sm underline">
          Mer information här.
        </a>
      </div>
    );
  }

  return undefined;
}

const BADGE_STYLE_BY_STATUS: Partial<Record<DrawStatus, Pick<BadgeProps, 'emphasis' | 'variant'>>> = {
  Live: { emphasis: 'low', variant: 'secondary' },
  Finished: { emphasis: 'low', variant: 'neutral' },
  Cancelled: { emphasis: 'high', variant: 'important' },
};

type DrawCardStatusMobileProps = {
  status: DrawStatus;
  isMyBetsOrResult?: boolean;
};

/**
 * Component for draw card status shown in draw card.
 *
 * @param root0 Component props
 * @param root0.status The status of the draw, which determines the text and styling shown in the component
 * @param root0.isMyBetsOrResult Whether the draw card is used in my bets or result view, which has different requirements for when to show status
 * @returns component that renders the status for a draw
 */
export default function DrawCardStatusMobile({ status, isMyBetsOrResult }: DrawCardStatusMobileProps): ReactNode {
  const drawCardStatus = getDrawCardStatus(status);
  const { emphasis: badgeEmphasis, variant: badgeVariant } = BADGE_STYLE_BY_STATUS[status] ?? {};

  return (
    <>
      {/* Mobile panel */}
      {!isMyBetsOrResult && shouldShowStatus(status) && (
        <div className={cn('flex flex-col items-center justify-between')}>
          <div className={cn('text-s text-surface-fg-muted', status === 'TemporaryClosed' && 'hidden')}>Status</div>
          {drawCardStatus}
        </div>
      )}

      {/* Mobile panel in my bets or result view */}
      {isMyBetsOrResult && shouldShowStatus(status) && (
        <>
          {status !== 'TemporaryClosed' && (
            <div className="flex">
              <Badge className="w-fit" emphasis={badgeEmphasis} variant={badgeVariant}>
                {drawCardStatus}
              </Badge>
            </div>
          )}
          {status === 'TemporaryClosed' && <div className="flex items-center">{drawCardStatus}</div>}
        </>
      )}
    </>
  );
}
